import React, { useState, useRef, useEffect } from 'react';
import { getApiUrl } from '../config/api';
import { Link } from 'react-router-dom';
import ProductPrice from './ProductPrice';

const DEMO_PRODUCT_IMAGE = 'https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=400&q=80';
const RECENT_SEARCH_KEY = 'vastra_recent_searches';

function getRecentSearches() {
  try {
    const data = localStorage.getItem(RECENT_SEARCH_KEY);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
}

function saveRecentSearches(obj) {
  localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(obj));
}

const SearchOverlay = ({ open, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recentProducts, setRecentProducts] = useState([]);
  const [showResults, setShowResults] = useState(false); // Track if user has searched
  const inputRef = useRef(null);
  const overlayRef = useRef(null);

  // Load recent search product details
  useEffect(() => {
    if (!open) return;
    const recent = getRecentSearches();
    const ids = Object.keys(recent);
    if (ids.length === 0) {
      setRecentProducts([]);
      return;
    }
    // Sort by count descending
    const sortedIds = ids.sort((a, b) => recent[b] - recent[a]);
    // Fetch product details for these IDs
    fetch(getApiUrl(`/api/products?ids=${sortedIds.join(',')}`))
      .then(res => res.json())
      .then(data => {
        let products = Array.isArray(data.products) ? data.products : Array.isArray(data) ? data : [];
        products = sortedIds.map(id => products.find(p => p._id === id)).filter(Boolean);
        setRecentProducts(products);
      })
      .catch(() => setRecentProducts([]));
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
    if (!open) {
      setQuery('');
      setResults([]);
      setError('');
      setShowResults(false);
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (overlayRef.current && !overlayRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onClose]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    setShowResults(true);
    try {
      const res = await fetch(getApiUrl(`/api/products/search?q=${encodeURIComponent(query)}`));
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setResults(Array.isArray(data) ? data : []);
      // Update recent search counts
      if (Array.isArray(data) && data.length > 0) {
        const recent = getRecentSearches();
        data.forEach(product => {
          if (product._id) {
            recent[product._id] = (recent[product._id] || 0) + 1;
          }
        });
        saveRecentSearches(recent);
        // Update recent products list
        const sortedIds = Object.keys(recent).sort((a, b) => recent[b] - recent[a]);
        fetch(getApiUrl(`/api/products?ids=${sortedIds.join(',')}`))
          .then(res => res.json())
          .then(data2 => {
            let products = Array.isArray(data2.products) ? data2.products : Array.isArray(data2) ? data2 : [];
            products = sortedIds.map(id => products.find(p => p._id === id)).filter(Boolean);
            setRecentProducts(products);
          });
      }
    } catch (err) {
      setError('Failed to fetch products.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Show recent searches if no search performed or query is cleared
  useEffect(() => {
    if (!query.trim()) {
      setShowResults(false);
      setResults([]);
    }
  }, [query]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center">
      <div className="w-full max-w-3xl mt-24 bg-white rounded-2xl shadow-2xl p-8 relative" ref={overlayRef}>
        <button
          className="absolute top-4 right-4 text-2xl text-gray-400 hover:text-pink-500 font-bold"
          onClick={onClose}
          aria-label="Close search"
        >
          ×
        </button>
        <form onSubmit={handleSearch} className="flex gap-4 mb-8 items-center">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => { if (!showResults) setShowResults(false); }}
            placeholder="Search products by name, tag, or category..."
            className="w-full px-6 py-4 rounded-full border-2 border-pink-400 focus:outline-none focus:ring-2 focus:ring-pink-400 text-lg shadow"
          />
          <button
            type="submit"
            className="px-8 py-3 rounded-full bg-gradient-to-r from-pink-500 to-indigo-500 text-white font-semibold shadow hover:from-pink-600 hover:to-indigo-600 transition"
          >
            Search
          </button>
        </form>
        {/* Recent Searches (show if not showing results) */}
        {!showResults && recentProducts.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 text-gray-700">Recent Searches</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {recentProducts.map(product => (
                <div key={product._id} className="bg-gray-50 rounded-2xl shadow hover:shadow-lg transition overflow-hidden border border-gray-100">
                  <Link to={`/product/${product._id}`} className="block">
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={product.images?.[0] || DEMO_PRODUCT_IMAGE}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-base font-semibold text-gray-900 mb-1 line-clamp-2">{product.name}</h4>
                      <div className="flex items-center justify-between">
                        <ProductPrice
                          product={product}
                          saleClassName="text-lg font-bold text-pink-600"
                          showPercent
                        />
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Search Results (show if search performed) */}
        {showResults && loading && <div className="text-center text-gray-500">Searching...</div>}
        {showResults && error && <div className="text-center text-red-500 mb-4">{error}</div>}
        {showResults && results.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            {results.map(product => (
              <div key={product._id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300 border border-gray-100">
                <div className="relative w-full h-64 overflow-hidden">
                  <img
                    src={product.images?.[0] || DEMO_PRODUCT_IMAGE}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4">
                  <Link to={`/product/${product._id}`} className="block">
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">{product.name}</h4>
                  </Link>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-gray-600">
                      {Array.isArray(product.reviews) ? product.reviews.length : 0} reviews
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <ProductPrice
                      product={product}
                      saleClassName="text-xl font-bold text-pink-600"
                      showPercent
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {showResults && (!loading && !error && results.length === 0 && query.trim()) && (
          <div className="text-center text-gray-500 mt-8">No products found.</div>
        )}
      </div>
    </div>
  );
};

export default SearchOverlay; 